import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";

export default function Cart() {
  const { items, setQty, removeItem, total, clear } = useCart();
  const { user } = useAuth();
  const { settings, currency } = useSettings();
  const [form, setForm] = useState({ customer_name: user?.name || "", phone: "", address: "", note: "" });
  const [placing, setPlacing] = useState(false);
  const [priority, setPriority] = useState(false);
  const isSub = user && user.is_subscriber;

  const buildWaMessage = () => {
    const lines = items.map((i) => `• ${i.name}${i.variant_label ? ` (${i.variant_label})` : ""} × ${i.qty} = ${currency}${i.price * i.qty}`);
    return encodeURIComponent(
      `*New Order — ${settings.store_name}*\n\n` +
        `${lines.join("\n")}\n\n` +
        `*Total: ${currency}${total}*\n\n` +
        `Name: ${form.customer_name}\n` +
        `Phone: ${form.phone}\n` +
        `Address: ${form.address}\n` +
        `Delivery: ${isSub ? "FREE (Subscriber)" + (priority ? " • Priority/Fast" : "") : "Standard"}\n` +
        (form.note ? `Note: ${form.note}\n` : "")
    );
  };

  const handleCheckout = async () => {
    if (!form.customer_name || !form.phone || !form.address) {
      toast.error("Please fill in your name, phone and address.");
      return;
    }
    if (items.length === 0) return;
    setPlacing(true);
    try {
      await api.post("/orders", {
        ...form,
        shipping_method: isSub && priority ? "priority" : "standard",
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          qty: i.qty,
          variant_id: i.variant_id || null,
          variant_label: i.variant_label || "",
        })),
      });
      const num = (settings.whatsapp_number || "").replace(/[^0-9]/g, "");
      const waUrl = `https://wa.me/${num}?text=${buildWaMessage()}`;
      window.open(waUrl, "_blank");
      toast.success("Order placed! Confirm it on WhatsApp.");
      clear();
      setForm({ customer_name: user?.name || "", phone: "", address: "", note: "" });
    } catch (e) {
      toast.error("Could not place order. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <ShoppingBag className="w-14 h-14 text-[#E8E4D9] mx-auto" strokeWidth={1.5} />
        <h1 className="mt-6 text-2xl font-display font-semibold text-[#1A231C]">Your cart is empty</h1>
        <p className="text-[#5C665D] mt-2">Browse our fresh groceries and fashion picks.</p>
        <Link
          to="/shop"
          data-testid="cart-empty-shop-link"
          className="inline-block mt-6 bg-[var(--brand-primary)] text-white px-6 py-3 rounded-lg font-medium hover:bg-[var(--brand-primary-hover)] transition-colors"
        >
          Start Shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#1A231C] font-display mb-8">
        Your Cart
      </h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* items */}
        <div className="lg:col-span-2 space-y-4">
          {items.map((i) => (
            <div
              key={i.key}
              data-testid={`cart-item-${i.key}`}
              className="flex items-center gap-4 bg-white border border-[#E8E4D9] rounded-xl p-4"
            >
              <img src={i.image_url} alt={i.name} className="w-20 h-20 rounded-lg object-cover" />
              <div className="flex-1">
                <h3 className="font-display font-medium text-[#1A231C]">{i.name}</h3>
                {i.variant_label && <p className="text-xs text-[var(--brand-primary)] font-medium">{i.variant_label}</p>}
                <p className="text-sm text-[#5C665D]">{currency}{i.price} {i.unit}</p>
              </div>
              <div className="flex items-center border border-[#E8E4D9] rounded-lg">
                <button
                  data-testid={`cart-decrease-${i.key}`}
                  onClick={() => setQty(i.key, i.qty - 1)}
                  className="p-2 hover:bg-[#F4EFE6] rounded-l-lg"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-8 text-center text-sm font-medium">{i.qty}</span>
                <button
                  data-testid={`cart-increase-${i.key}`}
                  onClick={() => setQty(i.key, i.qty + 1)}
                  className="p-2 hover:bg-[#F4EFE6] rounded-r-lg"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="w-20 text-right font-display font-semibold text-[#1A231C]">
                {currency}{i.price * i.qty}
              </div>
              <button
                data-testid={`cart-remove-${i.key}`}
                onClick={() => removeItem(i.key)}
                className="p-2 text-[var(--brand-accent)] hover:bg-[#F4EFE6] rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {/* checkout */}
        <div className="bg-white border border-[#E8E4D9] rounded-xl p-6 h-fit sticky top-24">
          <h2 className="font-display font-semibold text-lg text-[#1A231C] mb-4">Delivery details</h2>
          <div className="space-y-3">
            <input
              data-testid="checkout-name"
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              placeholder="Full name"
              className="w-full bg-white border border-[#E8E4D9] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary)]"
            />
            <input
              data-testid="checkout-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="Phone number"
              className="w-full bg-white border border-[#E8E4D9] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary)]"
            />
            <textarea
              data-testid="checkout-address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Delivery address"
              rows={2}
              className="w-full bg-white border border-[#E8E4D9] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary)]"
            />
            <input
              data-testid="checkout-note"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Note (optional)"
              className="w-full bg-white border border-[#E8E4D9] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-primary)]"
            />
          </div>

          <div className="border-t border-[#E8E4D9] mt-5 pt-4">
            {isSub ? (
              <div data-testid="sub-perks" className="bg-[#F1F7F2] border border-[var(--brand-primary)]/20 rounded-lg p-3 mb-3">
                <p className="text-sm font-medium text-[var(--brand-primary)]">★ Subscriber perks active</p>
                <p className="text-xs text-[#5C665D] mt-0.5">Free delivery applied.</p>
                <label className="flex items-center gap-2 mt-2 text-sm text-[#1A231C]">
                  <input data-testid="priority-shipping" type="checkbox" checked={priority} onChange={(e) => setPriority(e.target.checked)} />
                  Priority / Fast shipping
                </label>
              </div>
            ) : (
              <div className="flex items-center justify-between text-sm text-[#5C665D] mb-2">
                <span>Delivery</span>
                <span>Standard</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-[#5C665D]">Total</span>
              <span className="font-display font-bold text-xl text-[#1A231C]" data-testid="cart-total">
                {currency}{total}
              </span>
            </div>
          </div>

          <button
            data-testid="checkout-whatsapp-button"
            onClick={handleCheckout}
            disabled={placing}
            className="w-full mt-5 bg-[#25D366] text-white rounded-lg px-6 py-3 font-medium hover:bg-[#1EBE53] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <MessageCircle className="w-5 h-5" strokeWidth={1.5} />
            {placing ? "Placing..." : "Order via WhatsApp"}
          </button>
          <p className="text-xs text-[#5C665D] mt-3 text-center">
            Your order is saved and a pre-filled message opens in WhatsApp to confirm with the store.
          </p>
        </div>
      </div>
    </div>
  );
}
